import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import _ from 'lodash';
import InfoTooltip from '@components/core/Icon/InfoTooltip/Index';
import './index.less';

// Access jQuery as external dependency
const $ = window.$;

const appendSelectOptions = ($select, optionsList, selectedValue) => {
    optionsList.forEach((option) => {
        if (Array.isArray(option.children)) {
            const $optgroup = $('<optgroup></optgroup>').attr('label', option.text);
            option.children.forEach(({ text, id }) => {
                $optgroup.append(new Option(text, id));
            });
            $select.append($optgroup);
            return;
        }
        if (option.text === undefined || option.id === undefined) {
            return;
        }
        const isSelected = option.id == selectedValue;
        $select.append(new Option(option.text, option.id, isSelected, isSelected));
    });
};

const Select2 = forwardRef((props, externalRef) => {
    const {
        id,
        name,
        type,
        title,
        value,
        required,
        className,
        allowEmpty,
        allowHtml,
        autofocus,
        allowClear,
        clearOnChange,
        hideSearch,
        minimumInputLength,
        multiple,
        onChange,
        options,
        placeholder,
        resetValue,
        url,
        disabled,
        parentSelector,
        tooltip,
        hideFocusOutline,
        selectedCheckmarks = false, // mark selected options with a checkmark in the open dropdown
        templateSelection,
    } = props;

    const ref = useRef(null);
    const isDisabled = useRef(disabled);
    const initialized = useRef(false);
    
    useImperativeHandle(externalRef, () => ({
        focusInput: () => {
            if (ref.current && $(ref.current).data('select2')) {
                $(ref.current).select2('open');
            }
        },
        clearValue: () => {
            $(ref.current).val('').trigger('change');
        },
    }));

    useEffect(() => {
        isDisabled.current = disabled;
        // Removing the disabled attribute manually (issue is Firefox)
        if (!disabled) {
            setTimeout(() => $(ref.current).removeAttr('disabled'));
        }
    }, [disabled]);

    useEffect(() => {
        if (resetValue) {
            $(ref.current).val('').trigger('change');
        }
    }, [resetValue]);

    useEffect(() => {
        // only notify change the select2 (prevent to trigger 'change' event which is defined below)
        $(ref.current).val(value).trigger('change.select2');
    }, [value]);

    useEffect(() => {
        if (!initialized.current) return;
        const $select = $(ref.current);

        if (allowHtml) {
            $select.off('change').off('select2:unselecting').select2('destroy');
            $select.empty();
            if (allowEmpty) {
                $select.append(new Option('', ''));
            }
            const config = {
                placeholder,
                multiple,
                data: options,
                allowClear,
                escapeMarkup: (m) => m,
            };
            if (parentSelector && $select.closest(parentSelector).length) {
                config.dropdownParent = $select.closest(parentSelector);
            }
            if (hideSearch) {
                config.minimumResultsForSearch = -1;
            }
            $select.select2(config);
            if (selectedCheckmarks) {
                // Not the dropdownCssClass option: in the bundled select2 4.0.5 build that option
                // requires the absent compat/dropdownCss module and throws on init.
                $select.data('select2').$dropdown.addClass('select2-checkmarks');
            }
            $select.on('change', function () {
                const _value = $(this).val();
                if (_value === null || _value === undefined || _value === '' || isDisabled.current) {
                    return;
                }
                const selectedData = $select.select2('data')[0];
                if (props.tags) {
                    onChange(selectedData.id, { data: selectedData });
                } else if (selectedData) {
                    onChange(_value, { data: selectedData });
                } else {
                    onChange(_value);
                }
                if (clearOnChange) {
                    $select.val('').trigger('change');
                }
            });
            $select.on('select2:unselecting', function () {
                !isDisabled.current && onChange(null);
            });
            if (value != null && (multiple ? value.length : true)) {
                $select.val(value).trigger('change.select2');
            }
            return;
        }

        $select.empty();

        if (allowEmpty) {
            const option = new Option('', '');
            $select.append(option);
        }
        appendSelectOptions($select, options, value);

        $select.val(value).trigger('change.select2');
    }, [options, allowHtml]);

    useEffect(() => {
        const config = {
            placeholder,
            multiple: multiple,
            data: options,
            allowClear: allowClear,
        };

        if (parentSelector && $(ref.current).closest(parentSelector).length) {
            config.dropdownParent = $(ref.current).closest(parentSelector);
        }

        if (hideSearch) config.minimumResultsForSearch = -1;

        // Lets an option's text stay searchable while the closed control shows the short form.
        if (templateSelection) config.templateSelection = templateSelection;

        if (allowHtml) {
            config.escapeMarkup = (m) => m;
        }

        if (url) {
            config.ajax = {
                url: url,
                type: 'GET',
                dataType: 'json',
                delay: 300,
            };
            config.tags = props.tags;
            config.minimumInputLength = minimumInputLength;
            config.createTag = (params) => {
                if (params.term.trim() === '') {
                    return null;
                }
                return {
                    id: params.term,
                    text: props.createTagFormat.replace('{term}', params.term),
                    isNew: true,
                    value: params.term
                };
            };
        }

        const $select = $(ref.current).select2(config);
        // Styling hook only (selected options already carry aria-selected="true"): the class scopes
        // the checkmark CSS (index.less) to dropdowns that opted in. Set on the instance's dropdown
        // element, not via the dropdownCssClass option — in the bundled select2 4.0.5 build that
        // option requires the absent compat/dropdownCss module and throws on init.
        if (selectedCheckmarks) {
            $select.data('select2').$dropdown.addClass('select2-checkmarks');
        }
        // Set initial value after init so Select2 can resolve id -> text from data
        if (value != null && (multiple ? value.length : true)) {
            $select.val(value).trigger('change.select2');
        }

        $select.on('change', function (e) {
            const _value = $(this).val();
            if (_value === null || _value === undefined || _value === '' || isDisabled.current) {
                return;
            }
            const allData = $select.select2('data');
            const selectedData = allData[0];
            if (props.tags) {
                const selectedId = selectedData.id;
                onChange(selectedId, { data: selectedData });
            } else if (selectedData) {
                // For multiple selects, every selected option's data is needed so the consumer can
                // label all chips — passing only the first item leaves freshly added ones unresolved.
                onChange(_value, { data: multiple ? allData : selectedData });
            } else {
                onChange(_value);
            }

            if (clearOnChange) {
                $select.val('').trigger('change');
            }
        });

        $select.on('select2:unselecting', function () {
            !isDisabled.current && onChange(null);
        });

        $select.data('select2').on('results:message', function (params) {
            this.dropdown._resizeDropdown();
            this.dropdown._positionDropdown();
        });

        if (autofocus) {
            $select.on('select2:open', function () {
                const searchField = document.querySelector(
                    '.select2-container--open .select2-search__field'
                );
                if (searchField) {
                    searchField.focus();
                }
            });
        }

        initialized.current = true;

        return () => {
            $select
                .off('change')
                .off('select2:unselecting')
                .off('select2:open')
                .off('select2:select')
                .select2('destroy');
        };
    }, []);

    const Title = () => {
        if (!title) return null;
        return <>
            <label className={required ? 'form-control-required' : ''}>
                {title}
            </label>
            {tooltip && <InfoTooltip value={tooltip} />}
        </>
    };

    const wrapperClassName = [
        hideFocusOutline && 'select2--hide-focus-outline',
        className,
    ].filter(Boolean).join(' ');

    if (type === 'button') {
        return (
            <div className={`dropdown select2-button-container`} id='own-custom-message-dropdown'>
                <a className='link-button no-margin' onClick={() => $(ref.current).select2('open')}>
                    <span className={required ? 'form-control-required' : ''}>{title}</span>
                </a>
                <div className={`form-group ${wrapperClassName}`}>
                    <select
                        ref={ref}
                        id={id}
                        name={name}
                        className='form-control select2-block'
                        required={required}
                        multiple={multiple}
                        defaultValue={value ?? ''}
                        disabled={disabled}
                    >
                        <EmptyOption allow={allowEmpty} />
                    </select>
                </div>
            </div>
        );
    }

    return (
        <div className={`form-group ${wrapperClassName}`}>
            <Title />
            <select
                ref={ref}
                id={id}
                name={name}
                className='form-control select2-block'
                required={required}
                multiple={multiple}
                defaultValue={value ?? ''}
                disabled={disabled}
            >
                <EmptyOption allow={allowEmpty} />
            </select>
        </div>
    );
});

const EmptyOption = ({ allow }) => {
    if (allow) return <option></option>;
    return null;
};

Select2.defaultProps = {
    allowClear: true,
    allowEmpty: true,
    allowHtml: false,
    className: '',
    clearOnChange: false,
    hideSearch: false,
    id: null,
    minimumInputLength: 3,
    multiple: false,
    name: null,
    onChange: () => { },
    options: [], // [{id:1, text: ''}]
    placeholder: null,
    required: false,
    resetValue: false,
    tags: false,
    title: null,
    type: 'select', // select, button
    value: null,
    disabled: false,
    parentSelector: null,
    createTagFormat: '{term}',
    hideFocusOutline: false,
    templateSelection: null,
};

export default Select2;
